/**
 * Page Router Component
 * Dynamically renders pages based on currentPage state
 * This allows the sidebar to control navigation without React Router routes
 */

import React from 'react';
import { useAdmin } from '../../store/AdminContext';
import { useAuth } from '../../store/AuthContext';
import DashboardPage from '../../pages/DashboardPage';
import UsersPage from '../../pages/UsersPage';
import PredictionsOverviewPage from '../../pages/PredictionsOverviewPage';
import CreateMarketPage from '../../pages/CreateMarketPage';
import PendingApprovalPage from '../../pages/PendingApprovalPage';
import PendingKYCPage from '../../pages/PendingKYCPage';
import TicketsPage from '../../pages/TicketsPage';
import RiskPage from '../../pages/RiskPage';
import FinancePage from '../../pages/FinancePage';
import CompliancePage from '../../pages/CompliancePage';
import ContentPage from '../../pages/ContentPage';
import AdminAccountsPage from '../../pages/AdminAccountsPage';
import SettingsPage from '../../pages/SettingsPage';
import AuditLogPage from '../../pages/AuditLogPage';
import AnalyticsPage from '../../pages/AnalyticsPage';
import PushNotificationsPage from '../../pages/PushNotificationsPage';
import VouchersPage from '../../pages/VouchersPage';
import AffiliatePartnersPage from '../../pages/AffiliatePartnersPage';
import PlatformFeeConfigPage from '../../pages/PlatformFeeConfigPage';
import WalletCreditVouchersPage from '../../pages/WalletCreditVouchersPage';
import VersusAIPage from '../../pages/VersusAIPage';
import VersusExpertPage from '../../pages/VersusExpertPage';
import LiveBettingPage from '../../pages/LiveBettingPage';
import HedgeRiskPage from '../../pages/HedgeRiskPage';
import RemoteConfigPage from '../../pages/RemoteConfigPage';

const PageRouter: React.FC = () => {
  const { currentPage } = useAdmin();
  const { admin } = useAuth();

  // Map page IDs to components
  const renderPage = () => {
    switch (currentPage) {
      case 'dashboard':
        // Only show master dashboard for master admins
        // Other roles should see their specific dashboard
        if (admin?.role === 'master') {
          return <DashboardPage />;
        }
        // Redirect non-master admins to their role-specific dashboard
        switch (admin?.role) {
          case 'finance':
            return <FinancePage view="finance-dashboard" />;
          case 'risk':
            return <RiskPage view="risk-dashboard" />;
          case 'support':
            return <TicketsPage view="support-dashboard" />;
          case 'compliance':
            return <CompliancePage view="compliance-dashboard" />;
          case 'content':
            return <ContentPage view="content-dashboard" />;
          default:
            return <DashboardPage />;
        }
      case 'users-all':
      case 'users-verified':
      case 'users-banned':
        return <UsersPage view={currentPage} />;
      case 'users-pending':
        return <PendingKYCPage />;
      case 'predictions-overview':
        return <PredictionsOverviewPage />;
      case 'live-betting':
        return <LiveBettingPage />;
      case 'hedge-risk':
        return <HedgeRiskPage />;
      case 'create-market':
        return <CreateMarketPage />;
      case 'pending-approval':
        return <PendingApprovalPage />;
      case 'markets':
        return <PredictionsOverviewPage />;
      case 'tickets-all':
      case 'tickets-mine':
      case 'tickets-escalated':
      case 'live-chat':
      case 'user-lookup':
      case 'canned-responses':
        return <TicketsPage view={currentPage} />;
      case 'risk-dashboard':
      case 'network-graph':
      case 'risk-cases':
      case 'suspicious-tx':
      case 'chargebacks':
      case 'kyc-manual':
      case 'aml-queue':
      case 'behavioral':
      case 'velocity':
        return <RiskPage view={currentPage} />;
      case 'kyc-queue':
        return <PendingKYCPage />;
      case 'finance-dashboard':
      case 'payout-queue':
      case 'deposits':
      case 'withdrawals':
      case 'revenue-report':
      case 'share-earn':
      case 'fee-report':
      case 'export-reports':
        return <FinancePage view={currentPage} />;
      case 'compliance-dashboard':
      case 'training-status':
      case 'certificates':
      case 'internal-audits':
      case 'lccp-checklist':
      case 'limit-requests':
      case 'limits-reality':
      case 'self-exclusions':
        return <CompliancePage view={currentPage} />;
      case 'analytics':
        return <AnalyticsPage />;
      case 'admin-accounts':
        return <AdminAccountsPage />;
      case 'system-settings':
        return <SettingsPage />;
      case 'remote-config':
        return <RemoteConfigPage />;
      case 'audit-log':
        return <AuditLogPage />;
      case 'support-dashboard':
        return <TicketsPage view="support-dashboard" />;
      case 'content-dashboard':
      case 'content-reports':
      case 'market-proposals':
      case 'bet-ordering':
      case 'originals':
      case 'awards':
      case 'academy':
      case 'challenges':
      case 'campaigns':
        return <ContentPage view={currentPage} />;
      case 'notifications':
      case 'push-notifications':
        return <PushNotificationsPage />;
      case 'vouchers':
        return <VouchersPage />;
      case 'affiliate-partners':
        return <AffiliatePartnersPage />;
      case 'platform-fee-config':
        return <PlatformFeeConfigPage />;
      case 'wallet-credit-vouchers':
        return <WalletCreditVouchersPage />;
      case 'versus-ai':
        return <VersusAIPage />;
      case 'versus-expert':
        return <VersusExpertPage />;
      default:
        // For non-master admins, redirect to their role-specific dashboard
        if (admin?.role === 'master') {
          return <DashboardPage />;
        }
        // Redirect non-master admins to their appropriate dashboard
        switch (admin?.role) {
          case 'finance':
            return <FinancePage view="finance-dashboard" />;
          case 'risk':
            return <RiskPage view="risk-dashboard" />;
          case 'support':
            return <TicketsPage view="support-dashboard" />;
          case 'compliance':
            return <CompliancePage view="compliance-dashboard" />;
          case 'content':
            return <ContentPage view="content-dashboard" />;
          default:
            return <DashboardPage />;
        }
    }
  };

  return <>{renderPage()}</>;
};

export default PageRouter;

